/*
 * cart-terms-gate.js
 * ------------------------------------------------------------
 * Blocks checkout until the customer ticks the acknowledgement box rendered by
 * snippets/cart-terms-gate.liquid.
 *
 * Covered entry points:
 *   - cart PAGE  : button[name="checkout"] inside form.cart   (disabled attribute)
 *   - cart DRAWER: a[href="/checkout"] in .mini-cart-btns     (aria-disabled + click block)
 *
 * NOT covered (by design, same boundary as checkout-confirm.js): Shopify's native
 * accelerated checkout ("Buy it now" / Shop Pay / Apple Pay / PayPal) on the
 * product page, which bypasses the cart entirely.
 *
 * Everything is DELEGATED off `document` rather than wired per-element, because
 * the drawer's markup is destroyed and re-rendered by theme.miniCart.generateCart()
 * on every cart mutation — per-element listeners would be lost. A MutationObserver
 * re-applies the locked state to freshly injected buttons.
 *
 * The tick is remembered in sessionStorage so it survives the drawer re-rendering
 * and carries between the drawer and the cart page. It clears when the browser
 * session ends. To require a fresh tick on every page load instead, set
 * PERSIST to false.
 */
(function () {
  'use strict';

  var PERSIST = true;
  var STORAGE_KEY = 'mloft:cartTermsAccepted';

  var GATE = '[data-cart-terms-gate]';
  var CHECKBOX = '[data-cart-terms-checkbox]';
  var SCOPE = 'form.cart, .mini-cart-btns, [data-cart-terms-scope]';
  // Checkout triggers we gate. Buy-it-now / accelerated buttons are excluded.
  var TRIGGER = 'button[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]';

  function read() {
    if (!PERSIST) return false;
    try { return sessionStorage.getItem(STORAGE_KEY) === '1'; } catch (e) { return false; }
  }
  function write(v) {
    if (!PERSIST) return;
    try {
      if (v) sessionStorage.setItem(STORAGE_KEY, '1');
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  var accepted = read();

  // The elements a given gate governs: the checkout triggers in its scope.
  function triggersFor(gate) {
    var scope = gate.closest(SCOPE);
    if (!scope) return [];
    return Array.prototype.slice.call(scope.querySelectorAll(TRIGGER));
  }

  function lock(el, isLocked) {
    if (el.tagName === 'BUTTON') {
      // A real disabled attribute: blocks click, submit and keyboard activation.
      el.disabled = isLocked;
    } else {
      // <a> cannot be disabled. Mark it so CSS can grey it out; the capture-phase
      // interceptor below is the authoritative block. Deliberately NOT
      // pointer-events:none in CSS — the interceptor needs to receive the click in
      // order to block it, and it covers keyboard activation too.
      el.classList.toggle('is-terms-locked', isLocked);
      if (isLocked) el.setAttribute('aria-disabled', 'true');
      else el.removeAttribute('aria-disabled');
    }
  }

  // Push `accepted` onto every gate in the DOM (checkbox + its checkout triggers).
  function sync() {
    var gates = document.querySelectorAll(GATE);
    for (var i = 0; i < gates.length; i++) {
      var gate = gates[i];
      gate.classList.toggle('is-accepted', accepted);

      var box = gate.querySelector(CHECKBOX);
      if (box && box.checked !== accepted) box.checked = accepted;

      var triggers = triggersFor(gate);
      for (var j = 0; j < triggers.length; j++) lock(triggers[j], !accepted);
    }
  }

  function setAccepted(v) {
    accepted = !!v;
    write(accepted);
    sync();
  }

  // --- checkbox toggles (delegated: survives drawer re-render) ---------------
  document.addEventListener('change', function (e) {
    var box = e.target.closest ? e.target.closest(CHECKBOX) : null;
    if (!box) return;
    setAccepted(box.checked);
  });

  // --- policy links inside the <label> --------------------------------------
  // The sentence lives in a <label for>, because the theme's checkbox visual is
  // drawn with ::before/::after on .checkbox__label (it must be the input's next
  // sibling). Consequence: a click on a link inside it also activates the label
  // and toggles the box. stopPropagation() prevents that label activation while
  // leaving the anchor's own default action intact.
  document.addEventListener('click', function (e) {
    var link = e.target.closest ? e.target.closest('.cart-terms__link') : null;
    if (!link) return;

    e.stopPropagation();

    if (!link.classList.contains('js-cart-terms-policies')) return; // T&C: let it navigate

    // Policies: open the popup ourselves. Bootstrap's own data-toggle handler is
    // delegated on document and would be cut off by the stopPropagation above.
    e.preventDefault();
    if (window.jQuery && jQuery.fn && jQuery.fn.modal) {
      jQuery('#jsPoliciesModal').modal('show');
    } else {
      // No Bootstrap modal available — fall back to the Terms page rather than
      // leaving the link dead.
      var fallback = document.querySelector('#jsPoliciesModal');
      if (fallback) fallback.removeAttribute('aria-hidden');
    }
  });

  // --- the block itself -----------------------------------------------------
  // Capture phase, and this script MUST be loaded before checkout-confirm.js
  // (see snippets/header-js.liquid). That script also attaches a CAPTURE listener
  // to document, and capture listeners on the same node fire in registration
  // order — so whichever loads first wins. If checkout-confirm.js loads first, an
  // un-ticked click still pops the INR notice before this can stop it.
  // Once ticked, this is a no-op and that modal behaves exactly as before.
  document.addEventListener('click', function (e) {
    if (accepted) return;
    var trigger = e.target.closest ? e.target.closest(TRIGGER) : null;
    if (!trigger) return;

    // Block EVERY checkout trigger on the page, not only the ones inside a gated
    // scope. Three paths sit outside form.cart / .mini-cart-btns and were
    // otherwise free bypasses:
    //   - the INR notice's own "Proceed to checkout" link (#jsCheckoutConfirm
    //     .js-checkout-confirm-proceed). Double-clicking checkout opened that
    //     modal on the first click and landed the second click on Proceed.
    //   - the non-drawer mini-cart and cross-sell /checkout links.
    // A gate must exist somewhere in the document, otherwise this is a page with
    // no acknowledgement at all and nothing should be blocked.
    var scope = trigger.closest(SCOPE);
    var gate = (scope && scope.querySelector(GATE)) || document.querySelector(GATE);
    if (!gate) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    // No inline error copy (removed by request) — focusing the box is the only
    // feedback a blocked click gives.
    var box = gate.querySelector(CHECKBOX);
    if (box) box.focus();
  }, true);

  // Belt and braces: the cart page's form could be submitted by Enter in the
  // quantity field with `checkout` as the submitter.
  document.addEventListener('submit', function (e) {
    if (accepted) return;
    var form = e.target;
    if (!form.matches || !form.matches('form.cart')) return;
    if (!form.querySelector(GATE)) return;
    var submitter = e.submitter;
    // An `update` submit is fine — only block the checkout one.
    if (submitter && submitter.name !== 'checkout') return;
    if (!submitter) return;
    e.preventDefault();
  }, true);

  // --- keep the locked state applied to re-rendered markup ------------------
  function observe() {
    if (!window.MutationObserver) return;
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        sync();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    sync();
    observe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
